import { AlertCircle, Loader2, MapPin } from 'lucide-react'
import type { UseCompanyInfoTabReturn } from '@/features/company/types'
import styles from '../../../styles/companyAdd.module.css'
import {
    ZIPCODE_FORMAT_ERROR,
    isValidPhysicalZipcode,
} from '@/features/company/utils/companyInfoTabLogic'
import { SectionCard } from '@/components/ui/SectionCard'

export function CompanyInfoPhysicalAddressSection({
        register,
        errors,
        states,
        canEditInfo,
        isValidatingAddress,
        handleValidateAddress,
        handleLatLongLookup,
        handleSetGoogleAddress,
        handleAddressNotFound,
        showGoogleAddress,
        streetAddress,
        streetAddressRef,
        enableSetAddress,
        showLatLongLookup,
}: UseCompanyInfoTabReturn) {
    return (
            <SectionCard
                icon={MapPin}
                title="Physical Address"
                testId="company-info-section-physical-address"
            >
                <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>
                                Address Line1<span className={styles.requiredStar}>*</span>
                            </label>
                            <textarea
                                rows={2}
                                disabled={!canEditInfo}
                                {...register("address1", { required: "Address Line 1 is required" })}
                                data-testid="company-info-physical-address-line1-textarea"
                                className={`${styles.customFormControl} ${errors.address1 ? '!border-red-500' : ''}`}
                            />
                            {errors.address1 && (
                                <div className={styles.errorMessage}>
                                    <AlertCircle className="h-4 w-4" /> {errors.address1.message as string}
                                </div>
                            )}
                        </div>

                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>Address Line2</label>
                            <textarea
                                rows={2}
                                disabled={!canEditInfo}
                                {...register("address2")}
                                data-testid="company-info-physical-address-line2-textarea"
                                className={styles.customFormControl}
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>
                                City<span className={styles.requiredStar}>*</span>
                            </label>
                            <input
                                type="text"
                                disabled={!canEditInfo}
                                {...register("city", { required: "City is required" })}
                                data-testid="company-info-physical-city-input"
                                className={`${styles.customFormControl} ${errors.city ? '!border-red-500' : ''}`}
                            />
                            {errors.city && (
                                <div className={styles.errorMessage}>
                                    <AlertCircle className="h-4 w-4" /> {errors.city.message as string}
                                </div>
                            )}
                        </div>

                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>
                                Select State<span className={styles.requiredStar}>*</span>
                            </label>
                            <div className={styles.selectWrap}>
                                <select
                                    disabled={!canEditInfo}
                                    className={`${styles.customFormControl} ${errors.state ? '!border-red-500' : ''}`}
                                    {...register("state", { required: "State is required" })}
                                    data-testid="company-info-physical-state-select"
                                >
                                    <option value="">Select State</option>
                                    {states.map((state) => (
                                        <option key={state.state_code} value={state.state_code}>
                                            {state.state_name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            {errors.state && (
                                <div className={styles.errorMessage}>
                                    <AlertCircle className="h-4 w-4" /> {errors.state.message as string}
                                </div>
                            )}
                        </div>

                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>
                                Zipcode<span className={styles.requiredStar}>*</span>
                            </label>
                            <input
                                type="text"
                                disabled={!canEditInfo}
                                {...register("zipcode", {
                                    required: "Zipcode is required",
                                    validate: (v) => {
                                        const t = String(v ?? "").trim()
                                        if (!t) return true
                                        return isValidPhysicalZipcode(t) || ZIPCODE_FORMAT_ERROR
                                    },
                                })}
                                data-testid="company-info-physical-zipcode-input"
                                className={`${styles.customFormControl} ${errors.zipcode ? '!border-red-500' : ''}`}
                            />
                            {errors.zipcode && (
                                <div className={styles.errorMessage}>
                                    <AlertCircle className="h-4 w-4" /> {errors.zipcode.message as string}
                                </div>
                            )}
                        </div>
                    </div>
                    {canEditInfo && (
                        <div className="space-y-3 pt-1">
                            <div className="flex flex-wrap items-center gap-4">
                                <button
                                    type="button"
                                    data-testid="company-info-validate-address-button"
                                    onClick={handleValidateAddress}
                                    className={styles.btnPrimary}
                                >
                                    Validate Address
                                </button>
                                {showLatLongLookup && (
                                    <button
                                        type="button"
                                        data-testid="company-info-lat-long-lookup-button"
                                        onClick={() => void handleLatLongLookup()}
                                        disabled={isValidatingAddress}
                                        className={styles.btnPrimary}
                                    >
                                        {isValidatingAddress && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                        Lat/Long Lookup
                                    </button>
                                )}
                                <label style={{ display: "inline-flex", alignItems: "center", gap: "8px", fontSize: "14px", color: "#253c5c", cursor: "pointer" }}>
                                    <input
                                        type="checkbox"
                                        {...register("is_mailing_address")}
                                        data-testid="company-info-different-mailing-address-checkbox"
                                        style={{ width: "16px", height: "16px", cursor: "pointer" }}
                                    />
                                    Different Mailing Address?
                                </label>
                            </div>
                            {showGoogleAddress && (
                                <>
                                    <div className="max-w-xl">
                                        <input
                                            key="company-street-autocomplete"
                                            ref={streetAddressRef}
                                            type="text"
                                            defaultValue={streetAddress}
                                            placeholder="Enter your address"
                                            data-testid="company-info-street-address-input"
                                            className={styles.customFormControl}
                                        />
                                    </div>
                                    <div className="flex flex-wrap items-center gap-4">
                                        <button
                                            type="button"
                                            data-testid="company-info-set-address-button"
                                            onClick={handleSetGoogleAddress}
                                            disabled={!enableSetAddress}
                                            className={styles.btnPrimary}
                                        >
                                            Set Address
                                        </button>
                                        <button
                                            type="button"
                                            data-testid="company-info-address-not-found-button"
                                            onClick={handleAddressNotFound}
                                            className={styles.btnPrimary}
                                        >
                                            Not Found
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                </div>
            </SectionCard>
    )
}
